import { useCallback, useContext, useMemo, useRef, useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form } from '@unform/web'
import { AxiosError } from 'axios'
import { t } from 'i18next'

import { ButtonsCancelSave } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import KeywordListInput from '~/components/KeywordListInput'
import { KeywordListTotal } from '~/components/KeywordListTotal'
import { addKeywordsBulkService } from '~/modules/retailMedia/services/campaign-additional-keywords'

type FormNewCampaignKeyWordProps = {
  campaignId: string
  callback?: (data?: unknown) => void
  isNegative: boolean
  currentTotal: number
}

const TOTAL_KEYWORDS_LIMIT = 100

const FormNewCampaignKeyWord = ({
  campaignId,
  callback,
  isNegative,
  currentTotal
}: FormNewCampaignKeyWordProps) => {
  const { toast } = useContext(FeedbackContext)

  const [keywords, setKeywords] = useState<string[]>([])
  const QUERY_KEY = ['get-additional-keywords', campaignId, isNegative]
  const queryClient = useQueryClient()
  const errorsRef = useRef<string[]>([])

  const [pendingValues, setPendingValues] = useState<string[]>([])

  const setErrors = (value: string[]) => {
    errorsRef.current = value
  }

  const { mutateAsync: addKeywordMutation, isPending: isPendingAddKeyword } =
    useMutation({
      mutationFn: addKeywordsBulkService,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEY
        })

        setErrors([])

        toast.success(t('rm:campaign.form.new.keyword.success.message'))
        callback()
      },
      onError: (
        error: AxiosError<{
          error_code: string
          message: string
          custom_data: { conflicting_keywords: string[] }
        }>
      ) => {
        if (
          error?.response?.data?.error_code ===
          'CREATE_CAMPAIGN.CAMPAIGN_ADDITIONAL_KEYWORD_CONFLICTS'
        ) {
          const keywords_conflict =
            error?.response?.data?.custom_data?.conflicting_keywords
              ?.map(item => `"${item}"`)
              ?.join(', ')

          const conflictErrorMessage = t(
            'rm:business_errors.create_campaign.campaign_additional_keyword_conflicts',
            {
              values: keywords_conflict,
              listA: isNegative
                ? t('rm:negative_keywords')
                : t('rm:additional_keywords'),
              listB: !isNegative
                ? t('rm:negative_keywords')
                : t('rm:additional_keywords')
            }
          )

          if (keywords_conflict) {
            setErrors([conflictErrorMessage])
            toast.critical(conflictErrorMessage)
          } else {
            setErrors([])
          }
        } else {
          setErrors([])
          toast.critical(t('rm:campaign.form.new.keyword.error.message'))
        }
      }
    })

  const handleChangeKeywordsList = useCallback(
    ({ value }: { value: string[] }) => {
      setKeywords(value)
    },
    []
  )

  const handlePendingValues = useCallback(
    ({ value }: { value: string[] }) => {
      setPendingValues(
        value?.filter(item => {
          return !keywords?.includes(item)
        })
      )
    },
    [keywords]
  )

  const total = useMemo(
    () =>
      (currentTotal || 0) +
      (pendingValues?.length || 0) +
      (keywords?.length || 0),
    [currentTotal, pendingValues, keywords]
  )

  const validateBeforeCreate = useCallback(() => {
    const beforeCreateErrors: string[] = []
    if (total > TOTAL_KEYWORDS_LIMIT) {
      beforeCreateErrors.push(t('rm:campaign.keywords.errors.total_limit'))
      toast.critical(t('rm:campaign.keywords.errors.total_limit'))
    }
    return beforeCreateErrors
  }, [total])

  const handleSubmit = async () => {
    if (!keywords.length) {
      toast.critical(t('rm:campaign.form.new.keyword.warn.empty.message'))
      return
    }
    await addKeywordMutation({
      campaignId,
      keywords,
      negative: isNegative
    })
  }

  return (
    <section className="px-3 pb-3">
      <KeywordListTotal total={total} limit={TOTAL_KEYWORDS_LIMIT} />
      <Form onSubmit={handleSubmit}>
        <KeywordListInput
          name="keywords"
          label={
            isNegative
              ? t('rm:campaign.keywords.negative.title')
              : t('rm:campaign.keywords.additional.title')
          }
          helperText={
            isNegative
              ? t('rm:campaign.keywords.negative.description')
              : t('rm:campaign.keywords.additional.description')
          }
          placeholder={t('rm:campaign.keywords.input.placeholder')}
          onChange={handleChangeKeywordsList}
          errorsRef={errorsRef}
          onChangePendingValues={handlePendingValues}
          validateBeforeCreate={validateBeforeCreate}
        />

        <hr />

        <ButtonsCancelSave
          onCancelClick={callback}
          isLoading={isPendingAddKeyword}
        />
      </Form>
    </section>
  )
}

export default FormNewCampaignKeyWord
